import React, { useContext, useEffect, useRef, useState } from 'react';
import * as css from './userNotifications.module.scss';
import Icon, { IconType } from '../icon';
import {
  AlpNotification,
  AlpNotificationType,
  NotificationFunctionContext,
} from './notificationProvider';
import { useIntl } from 'react-intl';
import translations from './userNotifications.i18n';

type UserNotificationsProps = {
  notifications: AlpNotification[];
};

export const UserNotifications: React.FC<
  React.PropsWithChildren<UserNotificationsProps>
> = ({ notifications }) => {
  const { removeNotification } = useContext(NotificationFunctionContext);

  return (
    <div className={css.notificationContainer}>
      {notifications.map((notification) => (
        <Notify
          key={notification.id}
          notification={notification}
          removeNotification={removeNotification}
        />
      ))}
    </div>
  );
};

const Notify: React.FC<
  React.PropsWithChildren<{
    notification: AlpNotification;
    removeNotification: (notification: AlpNotification) => void;
  }>
> = ({ notification, removeNotification }) => {
  const { type, message } = notification;
  const [timer, setTimer] = useState(4000);
  const { formatMessage } = useIntl();
  const timeRef = useRef(timer);
  timeRef.current = timer;

  const handleClose = () => {
    removeNotification(notification);
  };

  useEffect(() => {
    if (
      type === AlpNotificationType.SUCCESS ||
      type === AlpNotificationType.WARNING
    ) {
      const notificationTimer = setTimeout(
        () => removeNotification(notification),
        timeRef.current,
      );
      return () => clearTimeout(notificationTimer);
    }
  }, [type, timer, notification, removeNotification]);

  function notificationStatus(type: AlpNotificationType) {
    switch (type) {
      case AlpNotificationType.SUCCESS:
        return formatMessage({
          id: translations.successTitle.id,
        });
      case AlpNotificationType.ERROR:
        return formatMessage({
          id: translations.errorTitle.id,
        });
      case AlpNotificationType.WARNING:
        return formatMessage({
          id: translations.warningTitle.id,
        });
      case AlpNotificationType.REMOVE_ALL:
        return formatMessage({
          id: translations.removeAllTitle.id,
        });
      default:
        return;
    }
  }

  const getNotificationStyle = () => {
    switch (type) {
      case AlpNotificationType.SUCCESS:
        return css.notificationSuccess;
      case AlpNotificationType.ERROR:
        return css.notificationError;
      case AlpNotificationType.WARNING:
        return css.notificationWarning;
      case AlpNotificationType.REMOVE_ALL:
        return css.notificationRemove;
    }
  };

  return (
    <div
      className={getNotificationStyle()}
      onMouseOver={(e) => setTimer(10000)}
      onMouseLeave={(e) => setTimer(4000)}
      onClick={() => {
        if (type === AlpNotificationType.REMOVE_ALL) {
          handleClose();
        }
      }}
    >
      {type !== AlpNotificationType.REMOVE_ALL && (
        <div className={css.notificationImage}>
          {type === AlpNotificationType.SUCCESS ? (
            <Icon type={IconType.Check} />
          ) : (
            <Icon type={IconType.Alert} />
          )}
        </div>
      )}
      <div>
        <p className={css.notificationStatus}>{notificationStatus(type)}</p>
        {type !== AlpNotificationType.REMOVE_ALL && (
          <p className={css.notificationMessage}>{message}</p>
        )}
      </div>
      {type !== AlpNotificationType.REMOVE_ALL && (
        <button onClick={handleClose}>
          <Icon type={IconType.Clear} />
        </button>
      )}
    </div>
  );
};

export default UserNotifications;
